/**
 * Course Notes & Training Material — installer view.
 *
 * Lists every PDF for the brand with Download and Open-in-new-tab buttons
 * per row. No inline preview — browsers handle PDF rendering in the new
 * tab, which works reliably across desktop + mobile.
 *
 * Reused across three brand routes via the `brand` URL param
 * (see App.js: /notes/probound, /notes/proepoxy, /notes/prodecor).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpenText, Download, ExternalLink, FileText, Home } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API_URL from '@/config/api';

const BRAND_META = {
  probound: { label: 'ProBound', accent: '#D4A24B', headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)', back: '/probound' },
  proepoxy: { label: 'ProEpoxy', accent: '#a78bfa', headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#5b21b6 80%)', back: '/proepoxy' },
  prodecor: { label: 'ProDecor', accent: '#fb923c', headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#9a3412 80%)', back: '/prodecor' },
};

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function CourseNotes() {
  const { brand: brandParam } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const brand = BRAND_META[brandParam] ? brandParam : 'probound';
  const theme = BRAND_META[brand];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/course-notes/${brand}`, { credentials: 'include' });
      if (res.status === 403) {
        toast.error('You don’t have access to this brand.');
        navigate('/');
        return;
      }
      if (!res.ok) throw new Error('Failed to load');
      setFiles(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brand, navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: theme.headerBg, borderBottom: `3px solid ${theme.accent}` }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(theme.back)} data-testid="notes-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label={`Back to ${theme.label}`}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <BookOpenText className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: theme.accent }} />
              {theme.label} Course Notes
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Course handbooks &amp; training material · download or open in a new tab</p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-3">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : files.length === 0 ? (
          <EmptyState theme={theme} />
        ) : (
          files.map(f => <FileRow key={f.id} file={f} theme={theme} brand={brand} />)
        )}
      </div>
    </div>
  );
}

function FileRow({ file, theme, brand }) {
  const previewUrl = `${API_URL}/api/course-notes/${brand}/file/${file.id}/download?t=${encodeURIComponent(file.uploaded_at || '')}`;
  const downloadUrl = `${API_URL}/api/course-notes/${brand}/file/${file.id}/download?download=1`;
  return (
    <Card className="flex flex-wrap items-center gap-3 p-3 sm:p-4" data-testid={`notes-row-${file.id}`}>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ background: theme.accent + '22', color: theme.accent }}>
        <FileText className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm sm:text-base font-bold text-gray-900 truncate" data-testid={`notes-filename-${file.id}`}>{file.filename}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {formatBytes(file.size_bytes)}
          {file.uploaded_by_name && <> · uploaded by <strong className="text-gray-700">{file.uploaded_by_name}</strong></>}
          {file.uploaded_at && <> · {new Date(file.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 w-full sm:w-auto">
        <Button asChild size="sm" data-testid={`notes-download-${file.id}`} className="font-semibold" style={{ background: theme.accent, color: brand === 'probound' ? '#0B0B0E' : '#fff' }}>
          <a href={downloadUrl} download={file.filename}><Download className="w-4 h-4 mr-1.5" />Download</a>
        </Button>
        <Button
          asChild
          variant="outline"
          size="sm"
          data-testid={`notes-open-new-${file.id}`}
          className="bg-white hover:bg-gray-50 focus:bg-white active:bg-white"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <a href={previewUrl} target="_blank" rel="noreferrer noopener"><ExternalLink className="w-4 h-4 mr-1.5" />Open in new tab</a>
        </Button>
      </div>
    </Card>
  );
}

function EmptyState({ theme }) {
  return (
    <Card className="p-10 text-center" data-testid="notes-empty">
      <div className="w-16 h-16 mx-auto mb-3 rounded-2xl flex items-center justify-center" style={{ background: theme.accent + '22', color: theme.accent }}>
        <BookOpenText className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-gray-900">No {theme.label} course notes yet</h2>
      <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
        The course notes haven’t been uploaded yet. Check back soon, or message the office.
      </p>
    </Card>
  );
}
